import type { LRUCache } from "@/cache/LRUCache";
import { TileLoadState, WmtsTile } from "@/cache/PVSTileService/TileGetters/WMTSTileGetter/WMTSTile";
import type { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import { WMTSRequestManager } from "@/layers/png/WMTSLayerImpl/WMTSRequestManager";
import { DEFAULT_TILE_SIZE } from "@/layers/png/WMTSLayerImpl/WMTSTileManager";
import { TilePriorityQueue } from "@/queue/WmtsTileQueue";
import { isPlaceholderTile } from "@/utility/typeGuard";
import { USE_DECLUTTERED_TRUE } from "@mm/api.meteomatics.com";

// const WMTS_API_BASE_URL = "https://api.meteomatics.workers.dev/tiles";
const WMTS_API_BASE_URL = "https://api.meteomatics.swiss/tiles";

export class WMTSRepository {
  private tileQueue = new TilePriorityQueue<WmtsTile>();
  private tileStates = new Map<string, "idle" | "queued" | "loading">();
  private inFlightTiles = new Map<string, WmtsTile>();
  private abortControllers = new Map<string, AbortController>();
  private requestManager = new WMTSRequestManager();
  private maxConcurrentLoads = 16;
  private activeLoads = 0;
  private processScheduled = false;

  constructor(
    readonly gl: WebGL2RenderingContext,
    readonly cache: LRUCache<WmtsTile>,
    private onTileReady: () => void, // TODO: Better way than a callback?
  ) {
    // Intentionally empty.
  }

  /**
   * Get the request manager for external access (debugging, stats)
   */
  getRequestManager(): WMTSRequestManager {
    return this.requestManager;
  }

  /**
   * Mark tiles as active for cancellation management (called by TileManager)
   */
  markTilesAsActive(tileKeys: string[]): void {
    this.cancelUnneededRequests();
    this.requestManager.markTilesAsActive(tileKeys);
  }

  /**
   * Get a tile from cache or create and start loading it
   */
  getTile(
    tileGeometry: TileGeometry,
    datetime: string,
    model: string,
    parameter: string,
    tileSize = DEFAULT_TILE_SIZE,
    colorMap = "jet",
    ensSelect = "none",
    vertical_interpolation: string | boolean = false,
    calibrated = false,
    initDate?: string,
    apiUrlSuffix?: string,
  ): WmtsTile {
    // Create tile to get cache key
    const tile = new WmtsTile(
      tileGeometry.x,
      tileGeometry.y,
      tileGeometry.z,
      datetime,
      model,
      parameter,
      tileSize,
      colorMap,
      ensSelect,
      vertical_interpolation,
      calibrated,
      initDate,
      apiUrlSuffix,
    );
    const cacheKey = tile.toKey();

    const cachedTile = this.cache.get(cacheKey);
    if (cachedTile) {
      if (isPlaceholderTile(cachedTile)) {
        if (Date.now() > cachedTile.placeholderUntil) {
          this.cache.del(cacheKey);
        } else {
          return cachedTile;
        }
      } else {
        return cachedTile;
      }
    }

    const inFlightTile = this.inFlightTiles.get(cacheKey);
    if (inFlightTile) {
      return inFlightTile;
    }

    // If the tile is not in the cache, queue it for loading with a lower priority.
    // A lower priority ensures that tiles currently in the viewport are loaded first.
    if (tile.loadState === TileLoadState.NotRequested) {
      this.queueTile(tile, 50);
    }

    return tile;
  }

  queueTile(tile: WmtsTile, priority: number) {
    const key = tile.toKey();
    const state = this.tileStates.get(key);
    if (state === "queued" || state === "loading") {
      this.inFlightTiles.set(key, this.inFlightTiles.get(key) ?? tile);
      return;
    }

    if (this.requestManager.isTerminalFailure(key)) {
      return;
    }

    // Check retry logic before queueing
    if (!this.requestManager.shouldRetryTile(key)) {
      return;
    }

    this.tileStates.set(key, "queued");
    this.inFlightTiles.set(key, tile);
    this.tileQueue.enqueue({ tile, priority });
    this.scheduleProcessQueue();
  }

  private addTilesToActiveSet(tileKeys: string[]): void {
    this.requestManager.markTilesAsActive(tileKeys);
    // Deliberately don't call cancelUnneededRequests()
  }

  reprioritize(tilesWithPriority: { tile: WmtsTile; priority: number }[], isAnimating: boolean) {
    // Mark these tiles as active (triggers cancellation of others)
    const activeTileKeys = tilesWithPriority.map(({ tile }) => tile.toKey());

    // If we are animating, we want to keep these tiles in the active set
    if (isAnimating) {
      this.addTilesToActiveSet(activeTileKeys);
    } else {
      this.addTilesToActiveSet(activeTileKeys);
    }

    // Clear the current priority queue
    this.tileQueue.clear();

    // Reset any "queued" tiles in the tileStates map
    for (const [key, state] of this.tileStates.entries()) {
      if (state === "queued") {
        this.tileStates.set(key, "idle");
        this.inFlightTiles.delete(key);
      }
    }

    // Add new prioritized tiles
    for (const { tile, priority } of tilesWithPriority) {
      this.queueTile(tile, priority);
    }
  }

  /**
   * Cancel requests for tiles no longer needed
   */
  public cancelUnneededRequests(): void {
    const toCancel: string[] = [];

    for (const [tileKey, _controller] of this.abortControllers.entries()) {
      if (!this.requestManager.isTileNeeded(tileKey)) {
        toCancel.push(tileKey);
      }
    }

    for (const tileKey of toCancel) {
      this.abortTileLoad(tileKey);
    }
  }

  private async processQueue() {
    while (this.activeLoads < this.maxConcurrentLoads && !this.tileQueue.isEmpty()) {
      const request = this.tileQueue.dequeue();
      if (!request) {
        return;
      }

      const tile = request.tile;
      const key = tile.toKey();

      // Skip if tile state changed or no longer needed
      if (this.tileStates.get(key) !== "queued" || !this.requestManager.isTileNeeded(key)) {
        this.inFlightTiles.delete(key);
        continue;
      }

      this.tileStates.set(key, "loading");
      this.activeLoads++;

      try {
        await this._loadTile(tile);
      } finally {
        this.tileStates.set(key, "idle");
        this.inFlightTiles.delete(key);
        this.abortControllers.delete(key);
        this.activeLoads--;
        this.processQueue(); // continue next
      }
    }
  }

  private scheduleProcessQueue() {
    if (this.processScheduled) {
      return;
    }
    this.processScheduled = true;

    queueMicrotask(() => {
      this.processScheduled = false;
      this.processQueue();
    });
  }

  private getAccessToken() {
    const authLocalStorageStr = window.localStorage.getItem("auth");
    if (!authLocalStorageStr) {
      return "";
    }
    return JSON.parse(authLocalStorageStr).token.access_token;
  }

  private _generateUrl(tile: WmtsTile): string {
    const token = this.getAccessToken();

    const verticalInterpolation =
      tile.vertical_interpolation === "none" ? `vertical_interpolation=${tile.vertical_interpolation}` : "";

    const ensSelect = tile.ensSelect ? `ens_select=${tile.ensSelect}` : "";

    const initDate = tile.initDate ? `init_date=${encodeURIComponent(tile.initDate)}` : "";
    const apiUrlSuffix = tile.apiUrlSuffix?.trim().replace(/^[?&]+/, "") ?? "";

    const path = `${WMTS_API_BASE_URL}/${tile.model}/${tile.parameter}/EPSG:3857/${tile.z}/${tile.x}/${tile.y}/${tile.datetime}/${tile.tileSize}.webp`;

    const params = [
      `access_token=${token}`,
      `style=${tile.colorMap}`,
      USE_DECLUTTERED_TRUE,
      verticalInterpolation,
      ensSelect,
      initDate,
      `calibrated=${tile.calibrated ? "true" : "false"}`,
    ];

    const baseUrl = `${path}?${params.filter(Boolean).join("&")}`;
    if (!apiUrlSuffix) {
      return baseUrl;
    }
    return `${baseUrl}&${apiUrlSuffix}`;
  }

  private async _loadTile(tile: WmtsTile): Promise<void> {
    const tileKey = tile.toKey();

    try {
      tile.setLoading();
      const url = this._generateUrl(tile);
      const controller = new AbortController();
      this.abortControllers.set(tileKey, controller);

      const response = await fetch(url, { signal: controller.signal });
      const contentType = response.headers.get("content-type") ?? "";
      // TODO: we should already prevent parameter like sat_* from requesting to API if they are in the future.
      // but would cost us an additional request to be sure https://api.meteomatics.com/get_time_range?source=<model>&parameters=<parameter_list>
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        this.requestManager.classifyAndRecord(
          new Error(`HTTP ${response.status}`),
          tileKey,
          response.status,
          body,
          contentType,
        );
        throw new Error(`HTTP ${response.status}`);
      }

      // HTTP 200 but not an image? (ServiceException returned as XML/HTML)
      if (!contentType.startsWith("image/")) {
        const body = await response.text().catch(() => "");
        this.requestManager.classifyAndRecord(new Error("Non-image content with 200"), tileKey, 200, body, contentType);
        throw new Error("Non-image content with 200");
      }

      const blob = await response.blob();
      const imageBitmap = await createImageBitmap(blob);

      tile.uploadImmediately(this.gl, imageBitmap);
      tile.setLoaded();
      // Clear placeholder metadata if present
      (tile as any).isPlaceholder = false;
      (tile as any).placeholderUntil = undefined;
      this.cache.put(tileKey, tile);

      // Clear any failure record on success
      this.requestManager.clearFailure(tileKey);

      this.onTileReady(); // TODO: Check if there's a better way than a callback

      imageBitmap.close();

      //unload if gpu memory gets above threshold
    } catch (e) {
      const error = e as Error;

      // Do not log AbortErrors; they are expected during reprioritization/cancellation
      if (error.name === "AbortError") {
        return;
      }

      const isTerminal = this.requestManager.isTerminalFailure(tileKey);
      if (isTerminal) {
        // Log a single warning per tile key to avoid console spam
        // If this key is marked as terminal (e.g., 404/ServiceException), upload a transparent placeholder
        // and cache it with a TTL so renderable tiles reuse it until expiry.

        // only for debug
        // console.warn(
        //   `Terminal (TTL) for ${tileKey} – data not available, using transparent placeholder until TTL expiry.`,
        // );
        try {
          const imageData = new ImageData(1, 1); // fully transparent pixel
          const bitmap = await createImageBitmap(imageData);
          tile.uploadImmediately(this.gl, bitmap);
          tile.setLoaded(); // counts as loaded for rendering
          // Mark as placeholder and cache it so subsequent frames reuse it.
          tile.isPlaceholder = true;
          const remainingTtl = this.requestManager.getTerminalTTLmsRemaining(tileKey);
          const fallbackTtl = 2 * 60_000; // 2 min baseline if manager has no record
          tile.placeholderUntil = Date.now() + (remainingTtl ?? fallbackTtl);

          // Cache the placeholder so renderable tiles reuse the same instance until TTL expires
          this.cache.put(tileKey, tile);

          this.onTileReady(); // notify that a tile became ready
          bitmap.close();
        } catch (placeholderErr) {
          console.error("Failed to create/upload transparent placeholder:", placeholderErr);
          tile.setFailed();
        }
        return; // exit; terminal state + placeholder cached; no failure record necessary
      }

      // Non-terminal (retriable) failure: mark failed and record for backoff/retry
      console.error(`Tile load failed (retriable) for ${tileKey}:`, error);
      tile.setFailed();
      this.requestManager.recordFailure(tileKey, error);
    }
  }

  abortTileLoad(tileKey: string) {
    this.abortControllers.get(tileKey)?.abort();
    this.abortControllers.delete(tileKey);
    this.inFlightTiles.delete(tileKey);
    this.tileStates.set(tileKey, "idle");
  }

  isTileReady(
    tileGeometry: TileGeometry,
    datetime: string,
    model: string,
    parameter: string,
    tileSize = 256,
    colorMap = "jet",
    ensSelect = "none",
    initDate?: string,
    apiUrlSuffix?: string,
  ): boolean {
    const tempTile = new WmtsTile(
      tileGeometry.x,
      tileGeometry.y,
      tileGeometry.z,
      datetime,
      model,
      parameter,
      tileSize,
      colorMap,
      ensSelect,
      false,
      false,
      initDate,
      apiUrlSuffix,
    );
    const tile = this.cache.get(tempTile.toKey());

    return tile?.isLoaded ?? false;
  }

  getQueueStats() {
    let queued = 0;
    let loading = 0;
    for (const state of this.tileStates.values()) {
      if (state === "queued") {
        queued++;
      }
      if (state === "loading") {
        loading++;
      }
    }

    // Include request manager stats
    const requestStats = this.requestManager.getStats();

    return {
      queued,
      loading,
      cacheSize: this.cache.map.size,
      activeLoads: this.activeLoads,
      ...requestStats,
    };
  }

  getCacheStats(): { size: number; memoryUsage: number; capacity: number } {
    return {
      size: this.cache.map.size,
      memoryUsage: this.cache.memoryFootprint,
      capacity: this.cache.capacity,
    };
  }
}
